'use client';

import './donate-huddle.scss';

import { FlexibleContentProps, Templates } from '@sola/schema/lib/schema.js';
import { TrackViewFund } from '@sola/tracking/components/datalayer.js';

import { Image } from '../../../atoms/image/image';
import { JsonLd } from '../../../atoms/jsonLd/jsonLd';
import {
  FeatureBase,
  FeatureBaseProps,
} from '../../../molecules/feature-base/feature-base';
import { FeatureButtons } from '../../../molecules/feature-buttons/feature-buttons';
import { buildFundSchema } from '../../../utils/schema-utils';
import { getBaseSizeProps } from '../../../utils/text-utils';
import { DonateProvider } from '../_logic/donate-context';
import { DonateForm } from '../_logic/donate-form';
import { DonateVariants } from '../_logic/donate.types';

export const DonateHuddle: React.FC<FlexibleContentProps> = ({
  seoLevel = 2,
  media,
  primaryCTA,
  secondaryCTA,
  tertiaryCTA,
  fund,
  mergeFieldData,
  ...props
}) => {
  const featureBaseProps = FeatureBaseProps.parse(props);

  const hasHeaderContent = !!featureBaseProps.title || !!featureBaseProps.body;
  const hasButtons = !!primaryCTA || !!secondaryCTA || !!tertiaryCTA;

  const shouldTrackView =
    fund &&
    (mergeFieldData?.pageData?.template === Templates.enum.giftCatalog ||
      mergeFieldData?.pageData?.template === Templates.enum.donate);

  return (
    <section className="DonateHuddle component">
      {shouldTrackView && <TrackViewFund fund={fund} />}
      <JsonLd
        item={{
          '@context': 'https://schema.org',
          '@type': 'DonateAction',
          ...(fund ? buildFundSchema(fund) : {}),
        }}
      />

      <div className="container">
        <div className="grid @md:grid-cols-2 gap-comfy-xl @lg:gap-roomy-sm">
          <div className="DonateHuddle__media">
            <Image
              className="DonateHuddle__image"
              image={media?.image}
              aspectRatio="4-3"
              aspectRatioMd="3-4"
              hasPlaceholder
              priority={seoLevel === 1}
            />
          </div>

          <div className="DonateHuddle__content flex flex-col gap-comfy-xl">
            <FeatureBase
              {...featureBaseProps}
              {...getBaseSizeProps(1)}
              seoLevel={seoLevel}
              bodySize="sm"
              inline
            />

            {fund && (
              <DonateProvider
                seoLevel={hasHeaderContent ? seoLevel + 1 : seoLevel}
                variant={DonateVariants.grid}
                fund={fund}
                {...props}
              >
                <DonateForm focusOnLoad />
              </DonateProvider>
            )}

            {hasButtons && (
              <FeatureButtons
                {...{ primaryCTA, secondaryCTA, tertiaryCTA }}
                alignment="default"
                className="justify-center @md:justify-start @md:mt-snug-xl"
                isLinks
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
